import { createAsyncThunk } from '@reduxjs/toolkit';
import { selectCurrentCompanySlug } from '@store/reducers/meReducer';
import { addErrorResponseNotification } from '@store/reducers/notificationReducer';

import type { DispatchActionType } from './DispatchActionType';
import { fetchAllPaginatedPages, fetchPaginatedPage, type PaginationConfig } from './paginationHelpers';
import {
  type InterfaceFunctionReplace,
  type InterfaceVarReplace,
  replaceUrlState,
  replaceUrlVariable,
} from './thunkHelperShared';

export interface InterfaceThunkGetPaginatedData<U = undefined | InterfaceVarReplace> {
  urlVarsReplaceMap?: U;
  page?: number;
  perPage?: number;
  paginationConfig?: PaginationConfig;
}

/**
 * Create a thunk that fetches ALL pages of paginated data
 * Returns a flat array of all items across all pages
 */
const createGetAllPagesThunk = <T, U = undefined | InterfaceVarReplace>(
  type: string,
  url: string,
  replaceMap: InterfaceFunctionReplace = {},
  successDispatchArray: DispatchActionType[] = [],
  errorDispatchArray: DispatchActionType[] = [],
) =>
  createAsyncThunk(
    `${type}/getAllPages/${url}`,
    async (data: InterfaceThunkGetPaginatedData<U> | undefined, { rejectWithValue, dispatch, getState }) => {
      try {
        let fullUrl = replaceUrlState(url, replaceMap, getState);
        if (data?.urlVarsReplaceMap !== undefined) {
          fullUrl = replaceUrlVariable(fullUrl, data.urlVarsReplaceMap as InterfaceVarReplace);
        }

        const allItems = await fetchAllPaginatedPages<T>(fullUrl, data?.paginationConfig);

        successDispatchArray.forEach((dispatchAction) => dispatch(dispatchAction));
        return allItems;
      } catch (error) {
        dispatch(addErrorResponseNotification({ error }));
        errorDispatchArray.forEach((dispatchAction) => dispatch(dispatchAction));
        throw rejectWithValue({ error });
      }
    },
  );

/**
 * Create a thunk that fetches a SINGLE page of paginated data
 * Returns the full paginated response with data, links, and meta
 */
const createGetSinglePageThunk = <T, U = undefined | InterfaceVarReplace>(
  type: string,
  url: string,
  replaceMap: InterfaceFunctionReplace = {},
  successDispatchArray: DispatchActionType[] = [],
  errorDispatchArray: DispatchActionType[] = [],
) =>
  createAsyncThunk(
    `${type}/getPage/${url}`,
    async (data: InterfaceThunkGetPaginatedData<U> | undefined, { rejectWithValue, dispatch, getState }) => {
      try {
        let fullUrl = replaceUrlState(url, replaceMap, getState);
        if (data?.urlVarsReplaceMap !== undefined) {
          fullUrl = replaceUrlVariable(fullUrl, data.urlVarsReplaceMap as InterfaceVarReplace);
        }

        const page = data?.page ?? 1;
        const perPage = data?.perPage ?? 100;

        const paginatedResponse = await fetchPaginatedPage<T>(fullUrl, page, perPage);

        successDispatchArray.forEach((dispatchAction) => dispatch(dispatchAction));
        return paginatedResponse;
      } catch (error) {
        dispatch(addErrorResponseNotification({ error }));
        errorDispatchArray.forEach((dispatchAction) => dispatch(dispatchAction));
        throw rejectWithValue({ error });
      }
    },
  );

/**
 * Company-scoped version that fetches ALL pages
 */
const createGetAllPagesThunkCompany = <T, U = undefined | InterfaceVarReplace>(
  type: string,
  url: string,
  successDispatchArray: DispatchActionType[] = [],
  errorDispatchArray: DispatchActionType[] = [],
) =>
  createGetAllPagesThunk<T, U>(
    type,
    url,
    { '{companySlug}': selectCurrentCompanySlug },
    successDispatchArray,
    errorDispatchArray,
  );

/**
 * Company-scoped version that fetches a SINGLE page
 */
const createGetSinglePageThunkCompany = <T, U = undefined | InterfaceVarReplace>(
  type: string,
  url: string,
  successDispatchArray: DispatchActionType[] = [],
  errorDispatchArray: DispatchActionType[] = [],
) =>
  createGetSinglePageThunk<T, U>(
    type,
    url,
    { '{companySlug}': selectCurrentCompanySlug },
    successDispatchArray,
    errorDispatchArray,
  );

/**
 * Thunk helpers for paginated endpoints
 *
 * Usage:
 * ```ts
 * // Fetch all pages (returns flat array of all items)
 * export const getCompanyCollectors = thunkGetPaginated.allPages.compUrl<InterfaceCollector>(
 *   sliceName,
 *   'c/{companySlug}/admin/collector'
 * );
 *
 * // Fetch single page (returns paginated response with meta)
 * export const getCompanyCollectorsPage = thunkGetPaginated.singlePage.compUrl<InterfaceCollector>(
 *   sliceName,
 *   'c/{companySlug}/admin/collector'
 * );
 *
 * // Dispatch with custom pagination config
 * dispatch(getCompanyCollectors({
 *   paginationConfig: {
 *     perPage: 50,
 *     maxPages: 5,
 *     delayBetweenPages: 100
 *   }
 * }));
 * ```
 */
export const thunkGetPaginated = {
  allPages: {
    replUrl: createGetAllPagesThunk,
    compUrl: createGetAllPagesThunkCompany,
    get: <T, U = undefined | InterfaceVarReplace>(
      type: string,
      url: string,
      successDispatchArray: DispatchActionType[] = [],
      errorDispatchArray: DispatchActionType[] = [],
    ) => createGetAllPagesThunk<T, U>(type, url, {}, successDispatchArray, errorDispatchArray),
  },
  singlePage: {
    replUrl: createGetSinglePageThunk,
    compUrl: createGetSinglePageThunkCompany,
    get: <T, U = undefined | InterfaceVarReplace>(
      type: string,
      url: string,
      successDispatchArray: DispatchActionType[] = [],
      errorDispatchArray: DispatchActionType[] = [],
    ) => createGetSinglePageThunk<T, U>(type, url, {}, successDispatchArray, errorDispatchArray),
  },
};

export default thunkGetPaginated;
